'use client'

import { Check, LayoutGrid, List } from 'lucide-react'

import { cn } from '@/lib/utils'

export type ViewMode = 'list' | 'grid'

type ViewModeToggleProps = {
  value: ViewMode
  onChange: (mode: ViewMode) => void
  className?: string
}

/** Pill list/grid toggle — shared across Library, Jobs, Apps, Projects. */
export function ViewModeToggle({ value, onChange, className }: ViewModeToggleProps) {
  return (
    <div
      className={cn(
        'inline-flex shrink-0 items-center overflow-hidden rounded-full border border-border bg-background',
        className
      )}
      role="group"
      aria-label="View mode"
    >
      <button
        type="button"
        className={cn(
          'inline-flex items-center gap-2 px-3 py-1.5 text-sm transition-colors',
          value === 'list'
            ? 'bg-muted/60 text-foreground'
            : 'text-muted-foreground hover:bg-muted/40'
        )}
        aria-pressed={value === 'list'}
        aria-label="List view"
        title="List view"
        onClick={() => onChange('list')}
      >
        {value === 'list' ? <Check className="h-4 w-4" /> : null}
        <List className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={cn(
          'inline-flex items-center gap-2 px-3 py-1.5 text-sm transition-colors',
          value === 'grid'
            ? 'bg-muted/60 text-foreground'
            : 'text-muted-foreground hover:bg-muted/40'
        )}
        aria-pressed={value === 'grid'}
        aria-label="Grid view"
        title="Grid view"
        onClick={() => onChange('grid')}
      >
        {value === 'grid' ? <Check className="h-4 w-4" /> : null}
        <LayoutGrid className="h-4 w-4" />
      </button>
    </div>
  )
}
